/**
 * Add comments for system fields in the Drizzle schema source code.
 * @param source The source code string to process
 * @returns The processed source code with system field comments added
 */
export function addSystemFieldComments(source: string): string {
  const commentMap: Record<string, string> = {
    '_created_at': 'Creation time',
    '_created_by': 'Creator',
    '_updated_at': 'Update time',
    '_updated_by': 'Updater',
  };

  const lines = source.split('\n');

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const entry = Object.entries(commentMap).find(([key]) => line.includes(`"${key}"`));
    if (!entry) {
      continue;
    }

    const [, description] = entry;
    const previousLine = lines[i - 1]?.trim() ?? '';
    if (previousLine.startsWith('//') && previousLine.includes('System field')) {
      continue;
    }

    const indentMatch = line.match(/^\s*/);
    const indent = indentMatch ? indentMatch[0] : '';
    const comment = `${indent}// System field: ${description} (auto-filled, do not modify)`;
    lines.splice(i, 0, comment);
    i += 1;
  }

  return lines.join('\n');
}

/**
 * Remove conflicting system fields from the Drizzle schema source code.
 * @param source The source code string to process
 * @returns The processed source code with conflicting system fields removed
 */
export function removeConflictingSystemFields(source: string): string {
  const systemFieldMap: Record<string, string> = {
    '_created_at': 'created_at',
    '_created_by': 'created_by',
    '_updated_at': 'updated_at',
    '_updated_by': 'updated_by',
  };

  const lines = source.split('\n');
  const result: Array<string | null> = [];

  // Track table-level state
  let inTable = false;
  let tableStartLine = -1;
  const tableBusinessFields = new Set<string>();
  let bracketDepth = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    // Check if we're starting a new table definition
    if (!inTable && /=\s*(pgTable|pgView|pgMaterializedView)\s*\(/.test(line)) {
      inTable = true;
      tableStartLine = result.length;
      tableBusinessFields.clear();
      bracketDepth = 0;
    }

    if (inTable) {
      // Track bracket depth to find table end
      for (const char of line) {
        if (char === '{') bracketDepth++;
        if (char === '}') bracketDepth--;
      }

      // Collect business fields in this table
      for (const businessField of Object.values(systemFieldMap)) {
        if (line.includes(`"${businessField}"`) || line.includes(`'${businessField}'`)) {
          tableBusinessFields.add(businessField);
        }
      }

      // Check if table definition ends
      if (bracketDepth === 0 && line.includes(');')) {
        inTable = false;

        // Now process this table's lines to remove conflicting system fields
        const tableEndLine = result.length;
        for (let j = tableStartLine; j <= tableEndLine; j++) {
          const tableLine = result[j] || '';
          let shouldRemove = false;

          for (const [systemField, businessField] of Object.entries(systemFieldMap)) {
            if (tableBusinessFields.has(businessField)) {
              if (tableLine.includes(`"${systemField}"`) || tableLine.includes(`'${systemField}'`)) {
                shouldRemove = true;
                // Also remove previous line if it's a system field comment
                if (j > 0 && result[j - 1]?.includes('// System field:')) {
                  result[j - 1] = null; // Mark for removal
                }
                break;
              }
            }
          }

          if (shouldRemove) {
            result[j] = null; // Mark for removal
          }
        }
      }
    }

    result.push(line);
  }

  // Filter out null entries (marked for removal)
  return result.filter(line => line !== null).join('\n');
}
